import { Fragment, useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { AlertTriangle } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useAuth, hasRole } from '../lib/auth';

type Alert = {
  id: string; department: string; message: string; created_by: string | null; created_at: string;
  link_kind?: 'message' | 'file' | 'email' | null; link_id?: string | null; link_dept?: string | null;
  co_message_id?: string | null; co_partner?: string | null; co_answered?: boolean;
};

// Mapea el depto de la alerta a la pestaña del claim donde vive el mensaje.
const DEPT_TAB: Record<string, string> = {
  claims: 'claims', payments: 'finance', supplements: 'supplements',
  scheduling: 'scheduling', intake: 'intake', contractor: 'contractor',
};

const DEPTS = ['all', 'claims', 'payments', 'supplements', 'scheduling', 'intake'];

/**
 * Banners rojos de alertas urgentes del claim (por depto). CUALQUIER usuario office+
 * puede crear una Y marcarla "No longer needed". Ambas acciones quedan en el activity log.
 */
export function ClaimAlerts({ claimId, onGo }: { claimId: string; onGo?: (tab: string, anchor: string | null) => void }) {
  const { t } = useI18n();
  const { me } = useAuth();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [open, setOpen] = useState(false);
  const [dept, setDept] = useState('all');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  // Qué alerta tiene el cuadro de respuesta abierto (pedido 2026-08-03: contestarle al contratista
  // desde la alerta, sin tener que ir a otra pantalla a buscar el mismo mensaje).
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const canResolve = hasRole(me, 'office');

  /** Responde al contratista desde la alerta. Al contestar, el backend marca el mensaje como
   *  respondido y apaga solo el aviso: la alerta desaparece de la lista sin tocar nada más. */
  async function sendReply(a: Alert) {
    if (!a.co_message_id || !replyText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/api/contractor-inbox/${a.co_message_id}/reply`, { body: replyText.trim() });
      setReplyTo(null); setReplyText('');
      await load();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  } // cualquier usuario staff puede quitar la alerta (queda en activity log)

  async function load() {
    try { setAlerts((await api.get<{ alerts: Alert[] }>(`/api/claims/${claimId}/alerts`)).alerts); } catch { /* ignore */ }
  }
  useEffect(() => { load(); }, [claimId]);

  /** Click en la alerta → abre la pestaña y salta al mensaje / archivo / email que la originó. */
  function goToSource(a: Alert) {
    if (!a.link_kind || !a.link_id) return;
    const tab = a.link_kind === 'file' ? 'files'
      : a.link_kind === 'email' ? 'email'
        : DEPT_TAB[a.link_dept ?? a.department] ?? 'overview';
    const anchor = a.link_kind === 'message' ? `msg-${a.link_id}` : a.link_kind === 'file' ? `file-${a.link_id}` : null;
    if (onGo) { onGo(tab, anchor); return; }
    // Sin callback (panel lateral): abre el claim completo en esa pestaña.
    window.location.assign(`/claims/${claimId}?tab=${tab}${anchor ? `#${anchor}` : ''}`);
  }

  async function create() {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post(`/api/claims/${claimId}/alerts`, { message: msg.trim(), department: dept });
      setMsg(''); setDept('all'); setOpen(false);
      await load();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  async function resolve(id: string) {
    try { await api.post(`/api/alerts/${id}/resolve`, {}); await load(); } catch { /* ignore */ }
  }

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {alerts.map((a) => (
        <Fragment key={a.id}>
        <div
          style={{
            // Variables del tema (feedback 2026-07-29: los colores fijos de light-mode
            // dejaban el banner ilegible en dark mode).
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10,
            background: 'var(--red-soft, #fdecea)',
            border: '1px solid color-mix(in srgb, var(--red, #d1553f) 35%, transparent)',
            borderLeft: '4px solid var(--red, #d1553f)',
          }}
        >
          <AlertTriangle size={18} style={{ color: 'var(--red, #c0392b)', flexShrink: 0 }} />
          <div
            style={{ flex: 1, minWidth: 0, cursor: a.link_kind ? 'pointer' : undefined }}
            role={a.link_kind ? 'button' : undefined}
            title={a.link_kind ? t('alert.goToSource') : undefined}
            onClick={() => goToSource(a)}
          >
            <span style={{ fontWeight: 600, color: 'var(--red, #a12f1e)', textDecoration: a.link_kind ? 'underline' : undefined, textUnderlineOffset: 2 }}>
              {a.message}{a.link_kind ? ' ↗' : ''}
            </span>
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
              {' '}· {a.department === 'all' ? t('alert.whole') : a.department}
              {a.created_by ? ` · ${a.created_by}` : ''}
              {/* CUÁNDO se creó la alerta (pedido: fecha y hora del envío) */}
              {' '}· {new Date(a.created_at).toLocaleString(localeUI(), { dateStyle: 'medium', timeStyle: 'short' })}
            </span>
          </div>
          {/* Responder AL CONTRATISTA sin salir de acá. Solo en las alertas que SON un mensaje suyo
              y que todavía no se contestaron. */}
          {a.co_message_id && !a.co_answered && canResolve && (
            <button className="btn sm" onClick={() => { setReplyTo(replyTo === a.id ? null : a.id); setReplyText(''); }}
              style={{ background: 'var(--accent)', color: '#fff', borderColor: 'transparent' }}>
              ↩ {t('alert.reply')}
            </button>
          )}
          {canResolve && <button className="btn ghost sm" onClick={() => resolve(a.id)}>{t('alert.resolve')}</button>}
        </div>
        {replyTo === a.id && (
          <div style={{ margin: '-4px 0 8px 30px', display: 'grid', gap: 6 }}>
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('alert.replyTo', { name: a.co_partner ?? '' })}</div>
            <textarea className="input" rows={3} autoFocus value={replyText} onChange={(e) => setReplyText(e.target.value)}
              placeholder={t('alert.replyPh')} style={{ fontSize: 'var(--fs-sm)', lineHeight: 1.5 }} />
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('alert.replyHint')}</span>
              <span style={{ flex: 1 }} />
              <button className="btn ghost sm" onClick={() => setReplyTo(null)}>{t('common.cancel')}</button>
              <button className="btn sm" disabled={busy || !replyText.trim()} onClick={() => sendReply(a)}>{t('alert.replySend')}</button>
            </div>
          </div>
        )}
        </Fragment>
      ))}

      {open ? (
        <div className="card" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="input" style={{ width: 150 }} value={dept} onChange={(e) => setDept(e.target.value)}>
            {DEPTS.map((d) => <option key={d} value={d}>{d === 'all' ? t('alert.whole') : d}</option>)}
          </select>
          <input className="input" style={{ flex: 1, minWidth: 200 }} placeholder={t('alert.messagePh')} value={msg} onChange={(e) => setMsg(e.target.value)} />
          <button className="btn" disabled={busy || !msg.trim()} onClick={create}>{t('alert.add')}</button>
          <button className="btn ghost" onClick={() => setOpen(false)}>{t('common.cancel')}</button>
        </div>
      ) : (
        <button className="btn ghost sm" style={{ justifySelf: 'start', color: 'var(--red, #c0392b)' }} onClick={() => setOpen(true)}>
          <AlertTriangle size={13} /> {t('alert.new')}
        </button>
      )}
    </div>
  );
}
